import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, Users, BarChart3, ArrowLeft, Search, Mail, Phone, Calendar, Link as LinkIcon, Instagram, Youtube, MessageCircle, TwitchIcon, DollarSign, FileText, ClipboardCheck, UserCheck } from 'lucide-react';
import { db, collection, query, where, getDocs, doc, getDoc } from '../lib/firebase';
import type { Agency, UserProfile, Client, Influencer, Collab, Platform } from '../lib/firebase';
import { Dialog } from '@headlessui/react';

const PLATFORM_ICONS = {
  Instagram: Instagram,
  YouTube: Youtube,
  TikTok: MessageCircle,
  Twitch: TwitchIcon,
};

export default function AgencyDetails() {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const [agency, setAgency] = useState<Agency | null>(null);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'clients' | 'influencers' | 'campaigns'>('users');
  const [search, setSearch] = useState('');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    const fetchAgencyData = async () => {
      try {
        setIsLoading(true);

        // Fetch agency details
        const agencyDoc = await getDoc(doc(db, 'agencies', agencyId!));
        if (!agencyDoc.exists()) {
          navigate('/superadmin');
          return;
        }
        setAgency({ id: agencyDoc.id, ...agencyDoc.data() } as Agency);

        // Fetch agency users
        const usersQuery = query(collection(db, 'users'), where('agencyId', '==', agencyId));
        const usersSnapshot = await getDocs(usersQuery);
        setUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as UserProfile));

        // Fetch agency clients
        const clientsQuery = query(collection(db, 'clients'), where('agencyId', '==', agencyId));
        const clientsSnapshot = await getDocs(clientsQuery);
        setClients(clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Client));

        // Fetch agency influencers
        const influencersQuery = query(collection(db, 'influencers'), where('agencyId', '==', agencyId));
        const influencersSnapshot = await getDocs(influencersQuery);
        setInfluencers(influencersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Influencer));

        // Fetch agency collaborations
        const collabsQuery = query(collection(db, 'collabs'), where('agencyId', '==', agencyId));
        const collabsSnapshot = await getDocs(collabsQuery);
        setCollabs(collabsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as Collab));

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching agency data:', error);
        setIsLoading(false);
      }
    };

    if (agencyId) {
      fetchAgencyData();
    }
  }, [agencyId, navigate]);

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  const getTotalRevenue = () => {
    return collabs.reduce((total, collab) => {
      const collabTotal = collab.influencers?.reduce((sum, inf) => 
        sum + parseFloat(inf.rate || '0'), 0) || 0;
      return total + collabTotal;
    }, 0);
  };

  const handleItemClick = (item: any, type: 'client' | 'influencer' | 'campaign') => {
    setSelectedItem({ ...item, type });
    setIsDetailModalOpen(true);
  };

  const getInfluencerCampaigns = (influencerId: string) => {
    return collabs.filter(collab => 
      collab.influencers.some(inf => inf.id === influencerId)
    );
  };

  const getClientCampaigns = (clientId: string) => {
    return collabs.filter(collab => 
      collab.brand === clients.find(c => c.id === clientId)?.brand_name
    );
  };

  const getCampaignStatus = (campaign: Collab) => {
    if (campaign.payment_received) return { label: 'Terminée', className: 'bg-green-500/20 text-green-300 border-green-500/20' };
    if (campaign.quote_sent) return { label: 'En cours', className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' };
    return { label: 'En attente', className: 'bg-blue-500/20 text-blue-300 border-blue-500/20' };
  };

  const renderDetailModal = () => {
    if (!selectedItem) return null;

    switch (selectedItem.type) {
      case 'client':
        return (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4">
              <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsDetailModalOpen(false)} />
              <div className="relative bg-dark-50 rounded-xl max-w-4xl w-full p-6 border border-dark-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">{selectedItem.brand_name}</h2>
                  <button onClick={() => setIsDetailModalOpen(false)} className="text-gray-400 hover:text-white">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Contacts</h3>
                    <div className="space-y-4">
                      {selectedItem.contacts.map((contact: any, index: number) => (
                        <div key={index} className="bg-dark-100 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            {contact.type === 'brand' ? (
                              <Building2 className="w-5 h-5 text-accent-blue" />
                            ) : (
                              <Users className="w-5 h-5 text-accent-purple" />
                            )}
                            <span className="text-white font-medium">{contact.name}</span>
                          </div>
                          <div className="space-y-2 pl-7">
                            <div className="flex items-center gap-2 text-gray-400">
                              <Mail className="w-4 h-4" />
                              <a href={`mailto:${contact.email}`} className="hover:text-white">
                                {contact.email}
                              </a>
                            </div>
                            {contact.phone && (
                              <div className="flex items-center gap-2 text-gray-400">
                                <Phone className="w-4 h-4" />
                                <a href={`tel:${contact.phone}`} className="hover:text-white">
                                  {contact.phone}
                                </a>
                              </div>
                            )}
                            {contact.role && (
                              <div className="text-gray-400">
                                Rôle: {contact.role}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Campagnes</h3>
                    <div className="space-y-4">
                      {getClientCampaigns(selectedItem.id).map((campaign) => (
                        <div key={campaign.id} className="bg-dark-100 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">{campaign.name}</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCampaignStatus(campaign).className}`}>
                              {getCampaignStatus(campaign).label}
                            </span>
                          </div>
                          <div className="text-gray-400 text-sm">
                            {formatDate(campaign.date)}
                          </div>
                          <div className="mt-2 text-gray-400">
                            {campaign.influencers.length} influenceur(s)
                          </div>
                          <div className="mt-2 text-accent-blue font-medium">
                            {formatCurrency(campaign.influencers.reduce((sum, inf) => sum + parseFloat(inf.rate || '0'), 0))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {selectedItem.notes && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-white mb-4">Notes</h3>
                    <div className="bg-dark-100 rounded-lg p-4">
                      <p className="text-gray-400 whitespace-pre-wrap">{selectedItem.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'influencer':
        return (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4">
              <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsDetailModalOpen(false)} />
              <div className="relative bg-dark-50 rounded-xl max-w-4xl w-full p-6 border border-dark-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">{selectedItem.fullName}</h2>
                  <button onClick={() => setIsDetailModalOpen(false)} className="text-gray-400 hover:text-white">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Plateformes</h3>
                    <div className="space-y-4">
                      {selectedItem.platforms.map((platform: Platform, index: number) => {
                        const Icon = PLATFORM_ICONS[platform.name as keyof typeof PLATFORM_ICONS] || MessageCircle;
                        return (
                          <div key={index} className="bg-dark-100 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Icon className="w-5 h-5 text-accent-blue" />
                              <span className="text-white font-medium">{platform.name}</span>
                            </div>
                            <div className="space-y-2 pl-7">
                              <div className="text-gray-400">
                                @{platform.handle}
                              </div>
                              <div className="text-gray-400">
                                Audience: {platform.audience}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <h3 className="text-lg font-medium text-white mb-4 mt-6">Tarifs</h3>
                    <div className="space-y-4">
                      {selectedItem.rates.map((rate: any, index: number) => (
                        <div key={index} className="bg-dark-100 rounded-lg p-4 flex items-center justify-between">
                          <span className="text-white">{rate.type}</span>
                          <span className="text-accent-blue font-medium">{formatCurrency(rate.price)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Campagnes</h3>
                    <div className="space-y-4">
                      {getInfluencerCampaigns(selectedItem.id).map((campaign) => (
                        <div key={campaign.id} className="bg-dark-100 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">{campaign.name}</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCampaignStatus(campaign).className}`}>
                              {getCampaignStatus(campaign).label}
                            </span>
                          </div>
                          <div className="text-gray-400">
                            {campaign.brand}
                          </div>
                          <div className="text-gray-400 text-sm">
                            {formatDate(campaign.date)}
                          </div>
                          <div className="mt-2 text-accent-blue font-medium">
                            {formatCurrency(
                              campaign.influencers
                                .find(inf => inf.fullName === selectedItem.fullName)?.rate || 0
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {selectedItem.internalNotes && (
                  <div className="mt-6">
                    <h3 className="text-lg font-medium text-white mb-4">Notes internes</h3>
                    <div className="bg-dark-100 rounded-lg p-4">
                      <p className="text-gray-400 whitespace-pre-wrap">{selectedItem.internalNotes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'campaign':
        return (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4">
              <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsDetailModalOpen(false)} />
              <div className="relative bg-dark-50 rounded-xl max-w-4xl w-full p-6 border border-dark-200">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedItem.name}</h2>
                    <p className="text-gray-400">{selectedItem.brand}</p>
                  </div>
                  <button onClick={() => setIsDetailModalOpen(false)} className="text-gray-400 hover:text-white">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Informations</h3>
                    <div className="bg-dark-100 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Calendar className="w-4 h-4" />
                        <span>Date: {formatDate(selectedItem.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <Users className="w-4 h-4" />
                        <span>Chef de projet: {selectedItem.manager}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <BarChart3 className="w-4 h-4" />
                        <span>Progression: {selectedItem.post_progress}%</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-400">
                        <DollarSign className="w-4 h-4" />
                        <span>Montant total: {formatCurrency(selectedItem.influencers.reduce((sum: number, inf: any) => sum + parseFloat(inf.rate || '0'), 0))}</span>
                      </div>
                    </div>

                    <h3 className="text-lg font-medium text-white mb-4 mt-6">État</h3>
                    <div className="bg-dark-100 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-400">
                          <FileText className="w-4 h-4" />
                          <span>Devis envoyé</span>
                        </div>
                        {selectedItem.quote_sent ? (
                          <div className="text-green-400">✓</div>
                        ) : (
                          <div className="text-red-400">×</div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-400">
                          <ClipboardCheck className="w-4 h-4" />
                          <span>Contrat signé</span>
                        </div>
                        {selectedItem.contract_signed ? (
                          <div className="text-green-400">✓</div>
                        ) : (
                          <div className="text-red-400">×</div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-400">
                          <UserCheck className="w-4 h-4" />
                          <span>Casting validé</span>
                        </div>
                        {selectedItem.casting_validated ? (
                          <div className="text-green-400">✓</div>
                        ) : (
                          <div className="text-red-400">×</div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-400">
                          <DollarSign className="w-4 h-4" />
                          <span>Paiement reçu</span>
                        </div>
                        {selectedItem.payment_received ? (
                          <div className="text-green-400">✓</div>
                        ) : (
                          <div className="text-red-400">×</div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Influenceurs</h3>
                    <div className="space-y-4">
                      {selectedItem.influencers.map((influencer: any, index: number) => (
                        <div key={index} className="bg-dark-100 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-white font-medium">{influencer.fullName}</span>
                            <span className="text-accent-blue font-medium">
                              {formatCurrency(influencer.rate || 0)}
                            </span>
                          </div>
                          <div className="space-y-2">
                            {influencer.deliverables?.map((deliverable: any, idx: number) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <span className="text-gray-400">{deliverable.type}</span>
                                <span className="text-gray-400">{formatCurrency(deliverable.price)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  if (!agency) {
    return null;
  }

  return (
    <div className="min-h-screen bg-dark p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/superadmin')}
              className="p-2 rounded-lg bg-dark-50 border border-dark-200 hover:bg-dark-100"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-white">{agency.name}</h1>
              <p className="text-gray-400">Créée le {formatDate(agency.createdAt)}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-dark-50 rounded-xl p-6 border border-dark-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-white">Utilisateurs</h3>
              <Users className="w-5 h-5 text-accent-blue" />
            </div>
            <p className="text-2xl font-bold text-white">{users.length}</p>
          </div>
          <div className="bg-dark-50 rounded-xl p-6 border border-dark-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-white">Clients</h3>
              <Building2 className="w-5 h-5 text-accent-purple" />
            </div>
            <p className="text-2xl font-bold text-white">{clients.length}</p>
          </div>
          <div className="bg-dark-50 rounded-xl p-6 border border-dark-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-white">Influenceurs</h3>
              <Users className="w-5 h-5 text-accent-blue" />
            </div>
            <p className="text-2xl font-bold text-white">{influencers.length}</p>
          </div>
          <div className="bg-dark-50 rounded-xl p-6 border border-dark-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-white">CA Total</h3>
              <BarChart3 className="w-5 h-5 text-accent-purple" />
            </div>
            <p className="text-2xl font-bold text-white">{formatCurrency(getTotalRevenue())}</p>
          </div>
        </div>

        <div className="bg-dark-50 rounded-xl border border-dark-200 p-4">
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'users'
                    ? 'bg-accent-blue text-white'
                    : 'bg-dark-100 text-gray-400 hover:bg-dark-200'
                }`}
              >
                Utilisateurs
              </button>
              <button
                onClick={() => setActiveTab('clients')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'clients'
                    ? 'bg-accent-blue text-white'
                    : 'bg-dark-100 text-gray-400 hover:bg-dark-200'
                }`}
              >
                Clients
              </button>
              <button
                onClick={() => setActiveTab('influencers')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'influencers'
                    ? 'bg-accent-blue text-white'
                    : 'bg-dark-100 text-gray-400 hover:bg-dark-200'
                }`}
              >
                Influenceurs
              </button>
              <button
                onClick={() => setActiveTab('campaigns')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'campaigns'
                    ? 'bg-accent-blue text-white'
                    : 'bg-dark-100 text-gray-400 hover:bg-dark-200'
                }`}
              >
                Campagnes
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 bg-dark-100 border border-dark-200 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            {activeTab === 'users' && (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Nom</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Email</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Rôle</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date d'inscription</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-200">
                  {users
                    .filter(user => 
                      user.firstName?.toLowerCase().includes(search.toLowerCase()) ||
                      user.lastName?.toLowerCase().includes(search.toLowerCase()) ||
                      user.email.toLowerCase().includes(search.toLowerCase())
                    )
                    .map(user => (
                      <tr key={user.id} className="hover:bg-dark-100/50">
                        <td className="py-3 px-4 text-white">
                          {user.firstName} {user.lastName}
                        </td>
                        <td className="py-3 px-4 text-gray-400">{user.email}</td>
                        <td className="py-3 px-4 text-gray-400">{user.role}</td>
                        <td className="py-3 px-4 text-gray-400">{formatDate(user.createdAt)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}

            {activeTab === 'clients' && (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Marque</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Contact principal</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Campagnes</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date d'ajout</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-200">
                  {clients
                    .filter(client => 
                      client.brand_name.toLowerCase().includes(search.toLowerCase())
                    )
                    .map(client => (
                      <tr 
                        key={client.id} 
                        className="hover:bg-dark-100/50 cursor-pointer"
                        onClick={() => handleItemClick(client, 'client')}
                      >
                        <td className="py-3 px-4 text-white">{client.brand_name}</td>
                        <td className="py-3 px-4 text-gray-400">
                          {client.contacts[0]?.name} • {client.contacts[0]?.email}
                        </td>
                        <td className="py-3 px-4 text-gray-400">
                          {getClientCampaigns(client.id!).length} campagne(s)
                        </td>
                        <td className="py-3 px-4 text-gray-400">{formatDate(client.createdAt)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}

            {activeTab === 'influencers' && (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Nom</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Plateformes</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Campagnes</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Référent</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date d'ajout</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-200">
                  {influencers
                    .filter(influencer => 
                      influencer.fullName.toLowerCase().includes(search.toLowerCase())
                    )
                    .map(influencer => (
                      <tr 
                        key={influencer.id} 
                        className="hover:bg-dark-100/50 cursor-pointer"
                        onClick={() => handleItemClick(influencer, 'influencer')}
                      >
                        <td className="py-3 px-4 text-white">{influencer.fullName}</td>
                        <td className="py-3 px-4 text-gray-400">
                          {influencer.platforms.map(p => p.name).join(', ')}
                        </td>
                        <td className="py-3 px-4 text-gray-400">
                          {getInfluencerCampaigns(influencer.id!).length} campagne(s)
                        </td>
                        <td className="py-3 px-4 text-gray-400">{influencer.internalReferent}</td>
                        <td className="py-3 px-4 text-gray-400">{formatDate(influencer.createdAt)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}

            {activeTab === 'campaigns' && (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Campagne</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Marque</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Montant</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-200">
                  {collabs
                    .filter(collab => 
                      collab.name.toLowerCase().includes(search.toLowerCase()) ||
                      collab.brand.toLowerCase().includes(search.toLowerCase())
                    )
                    .map(collab => (
                      <tr 
                        key={collab.id} 
                        className="hover:bg-dark-100/50 cursor-pointer"
                        onClick={() => handleItemClick(collab, 'campaign')}
                      >
                        <td className="py-3 px-4 text-white">{collab.name}</td>
                        <td className="py-3 px-4 text-gray-400">{collab.brand}</td>
                        <td className="py-3 px-4 text-gray-400">{formatDate(collab.date)}</td>
                        <td className="py-3 px-4 text-gray-400">
                          {formatCurrency(
                            collab.influencers?.reduce((sum, inf) => 
                              sum + parseFloat(inf.rate || '0'), 0) || 0
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCampaignStatus(collab).className}`}>
                            {getCampaignStatus(collab).label}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {renderDetailModal()}
    </div>
  );
}